import { Link, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { CrewListXpChip } from "@/components/crew/crew-list-xp-chip"
import { CrewRankChip } from "@/components/crew/crew-rank-chip"
import { LifecycleAlertBadge } from "@/components/crew/lifecycle-alert-badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { TableCell, TableRow } from "@/components/ui/table"
import { useInitials } from "@/hooks/use-initials"
import { cn } from "@/lib/utils"
import { crewMembershipPath } from "@/routes"
import type { CrewMember, LifecycleAlerts } from "@/types/crew"

/**
 * OKS-25 F8 — Single row in the Crew Roster (desktop ≥640px).
 *
 * Per UX Spec §Layout, columns are:
 *   1. Creator      — Avatar + display_name + @handle (stacked)
 *   2. Followers    — formatted ("24.5K") or `—` when null
 *   3. Engagement   — `5.2%` or `—` when null
 *   4. Location     — `City, CountryName` (country expanded via
 *                      `Intl.DisplayNames` for human-readable
 *                      country names; falls back to alpha-2)
 *   5. Tags         — first 3 chips + `+N` overflow chip
 *   6. Joined       — relative ("3d", "2w", "5mo")
 *
 * **OKS-26 — rows navigate to `crewMembershipPath(id)`.** OKS-25
 * shipped non-interactive rows because the detail page didn't exist;
 * OKS-26 closes that loop (PRD §Problem). Row-as-link pattern:
 *   - **Inner Inertia `<Link>`** wraps the Avatar + name + handle
 *     stack in the first cell. Carries the keyboard tab stop, focus
 *     ring, middle-click "open in new tab", and screen-reader
 *     "link" announcement — semantically valid HTML (`<a>` inside
 *     `<td>`, NOT around `<tr>` which would be invalid markup).
 *   - **Row-level `onClick`** captures whole-row clicks via
 *     `router.visit()`. A `target.closest("a, button")` guard
 *     prevents double-fire when the click hits the inner anchor or
 *     any future interactive descendant (e.g. inline status menu).
 *   - **`cursor-pointer hover:bg-muted/30`** surfaces the row as
 *     interactive on hover (UX Spec §Responsive — desktop only;
 *     mobile card variant ships its own full-card link wrapper).
 *
 * Locked testids (CONTRACT FROZEN by team-lead 2026-05-03):
 *   - `crew-list-row`            — every row (querySelectorAll target)
 *   - `crew-list-row-{id}`       — id-scoped row (Playwright targeting)
 *   - `data-member-id={id}`      — row data attr for QA filter idiom
 *   - `data-cell="{column}"`     — every `<td>` carries it for stable
 *                                   Playwright selectors without testid
 *                                   bloat (per FE TRD §QA pushback)
 */
interface CrewListRowProps {
  member: CrewMember
}

export function CrewListRow({ member }: CrewListRowProps) {
  const { t, i18n } = useTranslation()
  const getInitials = useInitials()

  const initials = getInitials(member.display_name)
  const followersText =
    member.follower_count === null
      ? "—"
      : formatFollowerCount(member.follower_count)
  const engagementText =
    member.engagement_rate === null
      ? "—"
      : `${member.engagement_rate.toFixed(1)}%`
  const locationText = formatLocation(member.location, i18n.language)
  const joinedRelative = formatJoinedRelative(member.joined_at, new Date())
  const detailHref = crewMembershipPath(member.id)

  /**
   * Whole-row click handler — navigates to the detail page when the
   * click target isn't an interactive element (Inner anchor / button
   * / future inline menu). Inertia's `router.visit` is preferred over
   * `<Link>`'s native `<a>` click because we can't wrap `<tr>` in an
   * `<a>` (invalid HTML).
   */
  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    if (!(e.target instanceof Element)) return
    if (e.target.closest("a, button")) return
    // Honor cmd-/ctrl-click conventions — let the browser handle them
    // (would be a no-op here since `<tr>` carries no `href`; the user
    // landed on the row click intent — fall back to a normal visit).
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    router.visit(detailHref)
  }

  return (
    <TableRow
      data-testid="crew-list-row"
      data-member-id={member.id}
      onClick={handleRowClick}
      className="hover:bg-muted/30 cursor-pointer transition-colors"
    >
      <TableCell data-cell="creator" data-testid={`crew-list-row-${member.id}`}>
        {/* OKS-27 — Creator cell wraps Link + lifecycle pills in a
            single flex-row so badges sit OUTSIDE the inner Inertia
            <Link> (UX V2 §Layout). Each pill is its own focusable
            <button>; the row-level `closest("a, button")` guard
            (handleRowClick) prevents badge clicks from double-firing
            the row visit. flex-wrap so FR-locale long handles wrap
            cleanly + badges drop to the next line at <640px. */}
        <div className="flex flex-wrap items-center gap-2">
          <Link
            href={detailHref}
            // The anchor expands inside the cell; row-level hover
            // already gives the visual feedback. Keyboard focus
            // surfaces the focus ring on the cell content.
            className="focus-visible:ring-ring/50 flex items-center gap-3 rounded-sm hover:no-underline focus-visible:ring-2 focus-visible:outline-none"
          >
            {/* OKS-656 — AvatarImage (Base UI) auto-swaps to AvatarFallback
                on load error → a rotated/expired TikTok CDN URL degrades to
                the initials, never a broken-image glyph (UX Spec §3 Error /
                PRD §6 resilience). A raw <img> here got no error handling. */}
            <Avatar className="size-9" aria-label={member.display_name}>
              {member.avatar_url && (
                <AvatarImage
                  src={member.avatar_url}
                  alt=""
                  className="rounded-full object-cover"
                />
              )}
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span data-cell="name" className="text-sm font-medium">
                {member.display_name}
              </span>
              <span
                data-cell="handle"
                className="text-muted-foreground text-xs tabular-nums"
              >
                @{member.tiktok_handle}
              </span>
            </div>
          </Link>
          <LifecycleAlertSlot alerts={member.lifecycle_alerts} surface="row" />
        </div>
      </TableCell>
      <TableCell data-cell="followers" className="tabular-nums">
        {followersText}
      </TableCell>
      <TableCell data-cell="engagement" className="tabular-nums">
        {/* OKS-48 — Per-brand XP chip stacked under the engagement
            value (UX Spec §2.3 — "under the existing engagement-rate
            row"). Renders 0 on empty-state per UX Spec §3 — never `—`. */}
        {/* OKS-49 — Rank chip lands to the right of the XP chip in the
            metric strip (UX Spec §2.2 — "Order in the metric strip:
            existing engagement chip → XP chip → rank chip"). Suppressed
            when `member.rank` is null (defensive — every brand has the
            default ladder seeded by Brand.after_create_commit). */}
        <div className="flex flex-col gap-0.5">
          <span>{engagementText}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <CrewListXpChip balance={member.xp_balance} />
            {member.rank && <CrewRankChip name={member.rank.name} />}
          </div>
        </div>
      </TableCell>
      <TableCell data-cell="location">
        <span className="inline-flex items-center gap-1.5">
          <span>{locationText}</span>
        </span>
      </TableCell>
      <TableCell data-cell="tags">
        <TagOverflow tags={member.tags} t={t} />
      </TableCell>
      <TableCell
        data-cell="joined"
        className="text-muted-foreground text-xs tabular-nums"
      >
        {joinedRelative}
      </TableCell>
    </TableRow>
  )
}

/**
 * Mobile (<640px) card view. Hides Location / Tags / Joined per UX
 * Spec §Responsive (the disclosure pattern lands on the detail page).
 * Still surfaces row testid + `data-cell` attrs so QA Playwright
 * works at the mobile viewport.
 *
 * **OKS-26 — full-card Inertia `<Link>`** wraps the entire card.
 * Valid HTML (`<a>` around `<div>`) — gives keyboard, focus ring,
 * middle-click "open in new tab", and screen-reader semantics for
 * free. No `onClick` plumbing needed; the native anchor handles
 * everything.
 */
export function CrewListRowCard({ member }: CrewListRowProps) {
  const getInitials = useInitials()
  const initials = getInitials(member.display_name)
  const followersText =
    member.follower_count === null
      ? "—"
      : formatFollowerCount(member.follower_count)
  const engagementText =
    member.engagement_rate === null
      ? "—"
      : `${member.engagement_rate.toFixed(1)}%`

  return (
    <Link
      href={crewMembershipPath(member.id)}
      data-testid="crew-list-row"
      data-member-id={member.id}
      className={cn(
        "border-border bg-card hover:bg-muted/30 focus-visible:ring-ring/50 flex items-center gap-3 rounded-lg border p-4 transition-colors hover:no-underline focus-visible:ring-2 focus-visible:outline-none",
      )}
    >
      <div data-testid={`crew-list-row-${member.id}`} className="contents">
        {/* OKS-656 — see desktop variant: AvatarImage → onError fallback. */}
        <Avatar className="size-10" aria-label={member.display_name}>
          {member.avatar_url && (
            <AvatarImage
              src={member.avatar_url}
              alt=""
              className="rounded-full object-cover"
            />
          )}
          <AvatarFallback>{initials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span data-cell="name" className="truncate text-sm font-medium">
            {member.display_name}
          </span>
          <span
            data-cell="handle"
            className="text-muted-foreground truncate text-xs tabular-nums"
          >
            @{member.tiktok_handle}
          </span>
          <div className="text-muted-foreground mt-1 flex flex-wrap gap-3 text-xs tabular-nums">
            <span data-cell="followers">{followersText}</span>
            <span data-cell="engagement">{engagementText}</span>
            {/* OKS-48 — XP chip on the mobile card variant (UX Spec §2.3). */}
            <CrewListXpChip balance={member.xp_balance} />
            {/* OKS-49 — Rank chip — last in the metric strip on mobile too. */}
            {member.rank && <CrewRankChip name={member.rank.name} />}
          </div>
          {/* OKS-27 — Lifecycle pills wrap below the handle row at
              <640px (UX V2 §Responsive). Pill clicks open the Radix
              tooltip and stop propagation; the surrounding card-Link
              still navigates on any non-pill click area. */}
          <LifecycleAlertSlot alerts={member.lifecycle_alerts} surface="row" />
        </div>
      </div>
    </Link>
  )
}

// ─── Pure formatting helpers (exported for direct unit tests) ──────

/**
 * Format a follower count as a human-readable short form:
 *   - <1k  → exact integer (e.g. `824`)
 *   - <1M  → `12.3K` (one decimal when needed; integer when ≥10K)
 *   - ≥1M  → `1.2M`
 *
 * Locale-agnostic — uses `Intl.NumberFormat` "compact" notation with
 * `en` so the suffix stays `K`/`M` (matches OKS-17 muscle memory and
 * avoids drift when the user toggles FR locale; the value is purely
 * numeric so locale-specific formatting isn't required for v1).
 */
export function formatFollowerCount(value: number): string {
  if (value < 1000) return String(value)
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value)
}

/**
 * Render the location cell. `country` is ISO-3166-1 alpha-2 on the
 * wire; expanded via `Intl.DisplayNames` to the user's locale. Falls
 * back to the alpha-2 code if the runtime can't resolve the region
 * (older browsers, unknown country code).
 *
 * Edge cases:
 *   - Both null → empty string (caller's `<TableCell>` renders blank)
 *   - city only → `"Paris"`
 *   - country only → `"France"` (or `"FR"` as fallback)
 *   - both → `"Paris, France"`
 */
export function formatLocation(
  location: {
    city: string | null
    country: string | null
  },
  locale: string,
): string {
  // OKS-348 — Localized country label via `Intl.DisplayNames`.
  const country = location.country
    ? expandCountry(location.country, locale)
    : null
  if (location.city && country) return `${location.city}, ${country}`
  if (location.city) return location.city
  if (country) return country
  return ""
}

export function expandCountry(alpha2: string, locale: string): string {
  try {
    const dn = new Intl.DisplayNames([locale], { type: "region" })
    return dn.of(alpha2) ?? alpha2
  } catch {
    return alpha2
  }
}

/**
 * Expand a wire-format composite location string (`"Paris, FR"`) into
 * its display form (`"Paris, France"` / `"Paris, France"` per locale)
 * for the Location filter Select option labels.
 *
 * The wire value (URL param + Select option `value`) MUST stay raw —
 * BE exact-matches against the alpha-2 composite. Display is the only
 * thing expanded, keeping a clean separation: wire = raw, UI = locale-
 * aware. Per UX review of F5.
 *
 * Falls back to the original composite string if the input isn't in
 * the expected `"City, XX"` shape.
 */
export function expandLocationComposite(
  composite: string,
  locale: string,
): string {
  // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
  const match = composite.match(/^(.+),\s*([A-Z]{2})$/)
  if (!match) return composite
  const [, city, alpha2] = match
  return `${city}, ${expandCountry(alpha2, locale)}`
}

/**
 * Compact relative-time formatter for the Joined column.
 *   - `<1 day`    → `Today`
 *   - `<1 week`   → `{n}d`
 *   - `<1 month`  → `{n}w`
 *   - `<1 year`   → `{n}mo`
 *   - `≥1 year`   → `{n}y`
 *
 * Pure function over the join date + a `now` reference (caller passes
 * `new Date()` in production; tests pass a frozen date for stable
 * assertions).
 */
export function formatJoinedRelative(joinedAtIso: string, now: Date): string {
  const joined = new Date(joinedAtIso)
  const diffMs = now.getTime() - joined.getTime()
  const day = 1000 * 60 * 60 * 24
  if (diffMs < day) return "Today"
  const days = Math.floor(diffMs / day)
  if (days < 7) return `${days}d`
  if (days < 30) return `${Math.floor(days / 7)}w`
  if (days < 365) return `${Math.floor(days / 30)}mo`
  return `${Math.floor(days / 365)}y`
}

// ─── OKS-27 — Lifecycle alert slot ─────────────────────────────────

/**
 * Renders the inactivity + rotation pills with the locked stacking
 * order (Inactivity → Rotation, severity descending — UX V2 lock).
 * `null` slots are omitted; surface-prefixed testids per FE TRD V4.
 *
 * `surface="row"` → `crew-list-row-alert-{rotation,inactivity}` +
 *                   shared row tooltip testid `crew-list-row-alert-tooltip`.
 * `surface="detail"` → `creator-detail-alert-{rotation,inactivity}` +
 *                   per-pill tooltip testids.
 *
 * **Fault tolerance** (UX Spec V2 §States — Error): when BE ships a
 * malformed `lifecycle_alerts` for a single row (object missing keys
 * or wrong shape), the slot logs to `console.error` with the locked
 * `lifecycle_alerts.malformed` tag and renders nothing — the row's
 * other content stays intact. NEVER blocks the row.
 */
export function LifecycleAlertSlot({
  alerts,
  surface,
}: {
  alerts: LifecycleAlerts | null | undefined
  surface: "row" | "detail"
}) {
  if (!isWellFormedLifecycleAlerts(alerts)) {
    if (alerts !== null && alerts !== undefined) {
      console.error("lifecycle_alerts.malformed", { alerts, surface })
    }
    return null
  }
  if (alerts.rotation === null && alerts.inactivity === null) return null

  const idPrefix =
    surface === "row" ? "crew-list-row-alert" : "creator-detail-alert"
  const inactivityTooltipId =
    surface === "row"
      ? "crew-list-row-alert-tooltip"
      : `${idPrefix}-inactivity-tooltip`
  const rotationTooltipId =
    surface === "row"
      ? "crew-list-row-alert-tooltip"
      : `${idPrefix}-rotation-tooltip`

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {/* Stacking order: Inactivity (red) → Rotation (amber) — UX
          V2 lock. Severity descending. DOM order matches visual order. */}
      <LifecycleAlertBadge
        variant="inactivity"
        alert={alerts.inactivity}
        testId={`${idPrefix}-inactivity`}
        tooltipTestId={inactivityTooltipId}
      />
      <LifecycleAlertBadge
        variant="rotation"
        alert={alerts.rotation}
        testId={`${idPrefix}-rotation`}
        tooltipTestId={rotationTooltipId}
      />
    </div>
  )
}

/**
 * Defensive shape guard. Trusts BE for happy paths but catches the
 * "missing key" / "string instead of null" / "stringly-typed `kind`"
 * regression class. Ships zero false positives — every legal wire
 * shape returns true; only structural drift returns false.
 */
function isWellFormedLifecycleAlerts(
  alerts: unknown,
): alerts is LifecycleAlerts {
  if (typeof alerts !== "object" || alerts === null) return false
  const a = alerts as Record<string, unknown>
  if (!("rotation" in a) || !("inactivity" in a)) return false
  if (a.rotation !== null && !isRotationShape(a.rotation)) return false
  if (a.inactivity !== null && !isInactivityShape(a.inactivity)) return false
  return true
}

function isRotationShape(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { months?: unknown }).months === "number"
  )
}

function isInactivityShape(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false
  const v = value as { kind?: unknown; weeks?: unknown }
  return (
    (v.kind === "post_mission" || v.kind === "joined_fallback") &&
    typeof v.weeks === "number"
  )
}

// ─── Tag overflow chip rendering ──────────────────────────────────

const TAG_RENDER_LIMIT = 3

interface TagOverflowProps {
  tags: string[]
  t: ReturnType<typeof useTranslation>["t"]
}

/**
 * Render up to `TAG_RENDER_LIMIT` tag chips. Any overflow is collapsed
 * into a single `+N` chip via `crew.list.row.tags_more` i18n key.
 */
function TagOverflow({ tags, t }: TagOverflowProps) {
  if (tags.length === 0) return null
  const visible = tags.slice(0, TAG_RENDER_LIMIT)
  const overflow = tags.length - visible.length
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((tag) => (
        <Badge key={tag} variant="secondary" className="text-xs">
          {tag}
        </Badge>
      ))}
      {overflow > 0 && (
        <Badge variant="outline" className="text-xs">
          {t("crew.list.row.tags_more", { count: overflow })}
        </Badge>
      )}
    </div>
  )
}
